import { Card } from "@mui/material";
import Fade from "@mui/material/Fade";
import moment from "moment";
import React, { useCallback, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import GlobalContext from "../../framework/GlobalContext";
import { searchCategories } from "../../services/category-services";
import classes from "./DoctorCommissionProgram.module.css";
import DoctorCommissionProgramActors from "./DoctorCommissionProgramActors";
import {
  doctorCommissionProgramColDef,
} from "./DoctorCommissionProgramConstants";
import { DoctorCommissionProgramContextProvider } from "./DoctorCommisionProgramContext";
import DoctorCommissionProgramGrid from "./DoctorCommissionProgramGrid";
import { createDoctorCommissionProgramItemFormData } from "./DoctorCommisionProgramUtils";
import { searchManufacturerGroup } from "../../services/manufacturer-group-services";
import { getDoctorCommissionDetails } from "../../services/doctor-commission";
import { doctorTypes } from "../../utils/constant";

const DoctorCommissionProgram = () => {
  const { autocompleteOpen, findCategory } = useContext(GlobalContext);
  const activeTabIndex = useSelector(
    (state) => state.commonReducer.activeTabIndex
  );
  const { props } = useSelector(
    (state) => state.commonReducer.tabs[state.commonReducer.activeTabIndex]
  );

  const { manufacturerGroupOptions, allBranches } = useSelector(
    (state) =>
      state.commonReducer.tabs[state.commonReducer.activeTabIndex].actors
  );

  const { categoryOptions } = useSelector((state) => state.commonReducer);

  const dispatch = useDispatch();
  const getManufacturerGroupOptions = useCallback(async () => {
    const rows = await searchManufacturerGroup({
      page: 1,
      limit: 20,
    });

    const options = (rows || []).map((g) => ({
      id: g.manufacturerGroupId,
      label: g.name,
    }));
    dispatch({
      type: "update_doctor_commission_program_actors",
      payload: {
        manufacturerGroupOptions: options,
      },
    });

    return options;
  }, [dispatch]);

  const focusField = useCallback((id) => {
    document.getElementById(id)?.focus({ preventScroll: true });
  }, []);

 
  const initializeDoctorCommissionProgram = useCallback(() => {
    if (props?.replicateDoctorCommissionProgramData) {
      dispatch({
        type: "update_doctor_commission_program_actors",
        payload: {
          name: props?.replicateDoctorCommissionProgramData?.programName,
          start: moment(props?.replicateDoctorCommissionProgramData?.startDate).format(
            "DD/MM/YYYY"
          ),
          end: moment(props?.replicateDoctorCommissionProgramData?.endDate).format(
            "DD/MM/YYYY"
          ),
        },
      });
      let rowData = [];
      if (
        Array.isArray(
          props?.replicateDoctorCommissionProgramData?.doctorCommissionDetails
        )
      ) {
        props?.replicateDoctorCommissionProgramData.doctorCommissionDetails.forEach(
          (item) => {
              const { doctorCommissionProgramDetailsId, ...rest } = item;
            let _category = findCategory(categoryOptions || [], item.typeId);
            rowData.push(
              createDoctorCommissionProgramItemFormData(
                {
                  ...rest,
                  option: {
                    value: _category,
                    categoryName: _category?.categoryName || "",
                    categoryId: item.categoryId,
                  },
                },
                true
              )
            );
          }
        );
      }
      dispatch({
        type: "update_doctor_commission_program_grid",
        payload: rowData,
      });
      return;
    }

    getDoctorCommissionDetails(
      `doctorCommissionId=${props?.doctorCommissionId}`
    ).then(async (resp) => {
      const data = resp?.result?.rows || resp || {};
      let rowData = [];
      const details = Array.isArray(data?.doctorCommissionDetails)
        ? data.doctorCommissionDetails
        : [];
      const matchingBranches =
        allBranches?.filter((branch) => {
          if (branch?.code) {
            return data?.branchCode?.includes(branch.code);
          }
          if (Array.isArray(branch?.branchCode)) {
            return branch.branchCode.some((code) =>
              data?.branchCode?.includes(code)
            );
          }
          return false;
        }) || [];

      const medicineForm = Array.isArray(resp?.result?.rows?.medicineForm) ? resp?.result?.rows?.medicineForm.map((item) => {
        return {
          id: item,
          label: item,
        };
      }) : [];

      const doctorType = resp?.result?.rows?.doctorType ? {
        id: resp?.result?.rows?.doctorType,
        label: doctorTypes.find((type) => type.id === resp?.result?.rows?.doctorType)?.label || resp?.result?.rows?.doctorType,
      } : null;

      const manufacturerGroup = resp?.result?.rows?.manufacturerGroupId ? {
        id: resp?.result?.rows?.manufacturerGroupId,
        label: manufacturerGroupOptions.find((option) => option.id === resp?.result?.rows?.manufacturerGroupId)?.label || resp?.result?.rows?.manufacturerGroupId,
      } : null;

      dispatch({
        type: "update_doctor_commission_program_actors",
        payload: {
          name: data?.doctorCommissionName,
          start: data?.startDate
            ? moment(data.startDate).format("DD/MM/YYYY")
            : null,
          end: data?.endDate ? moment(data.endDate).format("DD/MM/YYYY") : null,
          doctorCommissionId:
            data?.doctorCommissionId || props?.doctorCommissionId || null,
          branch: matchingBranches,
          medicineForm: medicineForm,
          doctorType: doctorType,
          manufacturerGroup: manufacturerGroup,
        },
      });

      details.forEach((item) => {
        rowData.push(
          createDoctorCommissionProgramItemFormData(
            {
              ...item,
            },
            true
          )
        );
      });
      dispatch({
        type: "update_doctor_commission_program_grid",
        payload: rowData,
      });
    });
    dispatch({ type: "update_props", payload: null });
  }, [
    props?.replicateDoctorCommissionProgramData,
    props?.doctorCommissionId,
    dispatch,
    findCategory,
    categoryOptions,
    allBranches,
    manufacturerGroupOptions,
  ]);

  const doctorCommissionProgramListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      const altKey = event.altKey;
      if (altKey) {
        if (altKey && key === "n") {
          focusField("doctor_commission_program_name");
          return;
        }
      }
    },
    [focusField]
  );

  useEffect(() => {
    getManufacturerGroupOptions();
  }, [
    getManufacturerGroupOptions,
  ]);

  useEffect(() => {
    searchCategories().then((response) => {
      if (response) {
        let updatedResp = [];
        response?.result?.rows?.forEach((cat) => {
          updatedResp.push({
            ...cat,
            id: cat.categoryId,
            name: cat.categoryName,
          });
        });
        dispatch({
          type: "update_category_options",
          payload: updatedResp,
        });
      }
    });
  }, [dispatch]);

  useEffect(() => {
    const dbData = localStorage.getItem("DoctorCommissionProgramGridColumns");
    if (!dbData) {
      localStorage.setItem(
        "DoctorCommissionProgramGridColumns",
        JSON.stringify({
          colId: 1,
          lastUpdated: new Date(),
          details: doctorCommissionProgramColDef,
        })
      );
    }
    dispatch({
      type: "update_grid_columnDefs",
      payload: dbData ? JSON.parse(dbData).details : doctorCommissionProgramColDef,
    });
  }, [dispatch, activeTabIndex]);

  useEffect(() => {
    if (props?.doctorCommissionId || props?.replicateDoctorCommissionProgramData) {
        initializeDoctorCommissionProgram(props?.doctorCommissionId);
    }
  }, [initializeDoctorCommissionProgram]);

  useEffect(() => {
    if (!autocompleteOpen)
      document.addEventListener("keyup", doctorCommissionProgramListener, false);
    return () => {
      document.removeEventListener("keyup", doctorCommissionProgramListener, false);
    };
  }, [autocompleteOpen, doctorCommissionProgramListener]);

  return (
    <DoctorCommissionProgramContextProvider>
      <Fade in={true} timeout={500}>
        <Card
          sx={{
            width: "100%",
            height: "100%",
            padding: "5px",
            borderRadius: 0,
          }}
        >
          <div
            className={`${classes["doctorCommissionProgram-medicine-bill-parent-wrapper"]}`}
          >
            <Card
              sx={{
                width: "100%",
                height: "100%",
                overflow: "visible",
                borderRadius: "4px",
              }}
            >
              <DoctorCommissionProgramGrid />
            </Card>
          </div>
          <Card
            sx={{
              width: "100%",
              height: "140px",
              border: 1,
              p: "0px 5px",
              transition: "500ms width ease-in",
              background: "#E0E0E0",
            }}
          >
            <DoctorCommissionProgramActors />
          </Card>
        </Card>
      </Fade>
    </DoctorCommissionProgramContextProvider>
  );
};
export default DoctorCommissionProgram;
